<script lang="ts" setup>
    import AppForm from '@/pages/core/form/AppForm.vue';
    import { useUserStore } from '@/stores/user';
    import { useI18n } from 'vue-i18n';
    import { useDisplay } from 'vuetify';
    import { userService } from '@/services/userService';

    const { locale, t } = useI18n();

    const display = useDisplay();
    const userStore = useUserStore();

    const dialogUploadDocuments = ref<boolean>(false);
    const infoDialog = ref<boolean>(false);
    const idDocument = ref(null);
    const utilityDocument = ref(null);
    const isValidUtility = ref<boolean>(false);
    const isValidId = ref<boolean>(false);
    const loading = ref<boolean>(false);
    const isError = ref<boolean>(false);
    const message = ref<string>('');
    const uploadDocumentsFormRef = ref<any>(null);

    const rules = {
        file: [
            (v: any): string | boolean => !!v || t('rules.required'),
            (v: any): string | boolean => {
                const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];
                if (v && !allowedTypes.includes(v.type)) {
                    return t('rules.fileAllowedTypes');
                }
                return true;
            },
            (v: any): string | boolean => {
                if (v && v.size > 3 * 1024 * 1024) {
                    return t('rules.fileSize', {
                        size: 3,
                    });
                }
                return true;
            },
        ],
    };

    const open = (): void => {
        // const form = uploadDocumentsFormRef.value.$refs.form;
        // form?.reset();
        // form?.resetValidation();
        depositAndWithdrawals();
    };

    const depositAndWithdrawals = (): void => {
        userService.checkDocuments().then((response: any) => {
            const data = response.data;

            if (data.success) {
                if (data.is_id_proof && data.is_utility_proof) {
                    setTimeout(() => {
                        window.open(`https://payments.tradeball.com/?token=${userStore.token}&lang=${locale.value}`, '_blank');
                    });
                } else if ((!data.is_id_proof && !data.is_valid_id) || (!data.is_utility_proof && !data.is_valid_utility)) {
                    isValidUtility.value = data.is_utility_proof || data.is_valid_utility;
                    isValidId.value = data.is_id_proof || data.is_valid_id;

                    dialogUploadDocuments.value = true;
                } else {
                    infoDialog.value = true;
                    message.value = t('menu.approvalMessage');
                }
            }
        });
    };

    const uploadDocuments = async (): Promise<void> => {
        const form = uploadDocumentsFormRef.value.$refs.form;

        const validateResult = await form?.validate();

        if (validateResult?.valid) {
            loading.value = true;
            const formData = new FormData();
            formData.append('idDocument', idDocument.value || '');
            formData.append('utilityDocument', utilityDocument.value || '');

            userService
                .uploadDocuments(formData)
                .then((response: any) => {
                    if (response.data.success) {
                        dialogUploadDocuments.value = false;
                        infoDialog.value = true;
                        message.value = t(response.data.message);
                    } else {
                        isError.value = true;
                        message.value = t(response.data.message);
                    }

                    idDocument.value = null;
                    utilityDocument.value = null;
                    loading.value = false;

                    form.reset();
                    form.resetValidation();
                })
                .catch((error: any) => {
                    isError.value = true;
                    message.value = t('common.uploadFailed');

                    if (typeof error === 'string' && error.includes('413 Request Entity Too Large')) {
                        message.value = t('user.maxFileSize');
                    }

                    loading.value = false;
                    form?.reset();
                    form?.resetValidation();
                });
        }
    };

    onMounted((): void => {
        console.log('Component app-menuAccountUploadDocumentsDialog mounted.');
    });

    defineExpose({ open });
</script>
<template>
    <v-layout>
        <v-dialog v-model="infoDialog" width="auto" :scrim="false">
            <v-card class="pa-3">
                <v-card-text class="font16 pa-2">{{ message }}</v-card-text>

                <v-card-actions class="pr-0 pb-0">
                    <v-spacer></v-spacer>
                    <v-btn density="default" tag="a" color="lightBlack" variant="text" @click="infoDialog = false">
                        {{ t('action.ok') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="dialogUploadDocuments" :fullscreen="display.xs.value" :scrim="false" persistent scrollable width="500">
            <v-card class="pa-3">
                <v-card-title class="py-1 pl-3 pr-1 d-flex justify-space-between align-center" style="height: 48px">
                    <span class="text-h5">{{ t('menu.verification') }}</span>
                    <v-spacer></v-spacer>
                    <v-btn density="default" tag="a" :icon="mdiClose" @click="dialogUploadDocuments = false" variant="text" size="36" />
                </v-card-title>

                <v-card-text class="py-0 pl-1 pr-2">
                    <AppForm ref="uploadDocumentsFormRef" @keyup.enter="uploadDocuments" class="pl-4 pr-4">
                        <v-container class="pl-0 pr-0">
                            <v-row>
                                <v-col cols="12" class="pl-2 pr-0 pt-0 pb-0">
                                    <span class="font14 bold">
                                        {{ t('menu.verificationOfYourAccount') }}
                                    </span>
                                    <br />
                                    <br />
                                    <span class="font14">
                                        {{ t('menu.verificationText') }}
                                    </span>
                                    <br />
                                    <br />
                                    <span class="font14">
                                        {{ t('menu.labelDocuments') }}
                                    </span>
                                    <br />
                                    <br />
                                    <div class="pl-4 font13 pb-4" v-if="!isValidId">
                                        <span class="bold">
                                            {{ t('menu.idDocument') }}
                                        </span>
                                        <span>
                                            {{ t('menu.labelIdDocument') }}
                                        </span>
                                        <v-file-input
                                            show-size
                                            :hint="t('menu.allowedTypes')"
                                            persistent-hint
                                            class="pt-2"
                                            v-model="idDocument"
                                            :label="t('menu.idDocument')"
                                            accept="image/png, image/jpeg, application/pdf"
                                            truncate-length="35"
                                            :rules="rules.file"
                                            require
                                        ></v-file-input>
                                    </div>
                                    <div class="pl-4 font13 pb-4" v-if="!isValidUtility">
                                        <span class="bold">
                                            {{ t('menu.utilityDocument') }}
                                        </span>
                                        <span>
                                            {{ t('menu.labelUtilityDocument') }}
                                        </span>
                                        <v-file-input
                                            show-size
                                            :hint="t('menu.allowedTypes')"
                                            persistent-hint
                                            class="pt-2"
                                            v-model="utilityDocument"
                                            :label="t('menu.utilityDocument')"
                                            accept="image/png, image/jpeg, application/pdf"
                                            truncate-length="35"
                                            :rules="rules.file"
                                            require
                                        ></v-file-input>
                                    </div>
                                    <span class="font14">
                                        {{ t('menu.reviewText') }}
                                    </span>
                                </v-col>
                                <div class="pl-2 pr-2 pt-2 pb-0 upload-error" v-show="isError">
                                    <span>{{ message }}</span>
                                </div>
                            </v-row>
                        </v-container>
                    </AppForm>
                </v-card-text>
                <v-card-actions class="pb-0 pl-0 pr-2">
                    <v-spacer></v-spacer>
                    <v-btn density="default" tag="a" :loading="loading" :disabled="loading" color="tbGreen" style="color: white" elevation="0" @click="uploadDocuments" variant="flat">
                        <span class="bold">
                            {{ t('action.submitVerification') }}
                        </span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-layout>
</template>
<style scoped>
    .upload-error span {
        font-size: 12px;
        color: #ef3b3a;
    }
</style>
